import {useLayoutEffect, useRef} from 'react';

/** Reveal each off-screen section once, without delaying the first viewport. */
export function useScrollReveal(pageKey?: string) {
    const pageRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const page = pageRef.current;
        if (!page || !('IntersectionObserver' in window)) return;

        const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const registered = new Set<HTMLElement>();
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                const section = entry.target as HTMLElement;
                section.classList.remove('reveal-pending');
                section.classList.add('reveal-visible');
                observer.unobserve(section);
            });
        }, {rootMargin: '0px 0px 48px 0px', threshold: 0});

        const registerSections = () => {
            if (motionPreference.matches) return;
            const sections = Array.from(page.querySelectorAll<HTMLElement>('[data-reveal]'))
                .filter(section => !registered.has(section));
            // Read positions together before setting classes, so layout isn't recalculated per section.
            const positions = sections.map(section => section.getBoundingClientRect());
            sections.forEach((section, index) => {
                registered.add(section);
                if (positions[index].top < window.innerHeight + 48) return;
                section.classList.add('reveal-pending');
                observer.observe(section);
            });
        };

        const revealAll = () => {
            if (!motionPreference.matches) {
                registerSections();
                return;
            }
            observer.disconnect();
            registered.forEach(section => section.classList.remove('reveal-pending', 'reveal-visible'));
        };
        const finishReveal = (event: AnimationEvent) => {
            if (event.animationName === 'section-enter' && event.target instanceof HTMLElement) {
                event.target.classList.remove('reveal-visible');
            }
        };

        registerSections();
        // Lazy routes can resolve after their container has mounted.
        const mutations = new MutationObserver(registerSections);
        mutations.observe(page, {childList: true, subtree: true});
        motionPreference.addEventListener('change', revealAll);
        page.addEventListener('animationend', finishReveal);

        return () => {
            observer.disconnect();
            mutations.disconnect();
            motionPreference.removeEventListener('change', revealAll);
            page.removeEventListener('animationend', finishReveal);
            registered.forEach(section => section.classList.remove('reveal-pending', 'reveal-visible'));
        };
    }, [pageKey]);

    return pageRef;
}

export default useScrollReveal;
